import React, { useEffect, useRef, useState } from 'react';
import {
  AssistantRuntimeProvider,
  useLocalRuntime,
  ThreadPrimitive,
} from '@assistant-ui/react';
import {
  AGENT_SESSION_REFRESH_THRESHOLD_SECONDS,
  podAdapter,
  getStoredConversationId,
  fetchHistoryMessages,
  rememberThreadMessages,
  ensureAgentSessionFresh,
} from './PodAdapter.js';
import { ConversationSwitcher } from './ConversationSwitcher.jsx';
import {
  buildInjectedHubPageContext,
  collectHubPageContext,
  setCurrentHubPageContext,
} from './PageContext.js';
import {
  HubAgentContext,
  useHubAgentState,
  CloseIcon,
  CopyIcon,
  ExpandIcon,
  MonitorIcon,
  PasteIcon,
  ShrinkIcon,
  AgentIdentityMark,
  AgentAvatar,
  Composer,
  UserMessage,
  AssistantMessage,
} from './shared.jsx';

function Thread() {
  return (
    <ThreadPrimitive.Root className="hub-thread">
      <ThreadPrimitive.Viewport className="hub-thread-viewport">
        <ThreadPrimitive.Empty>
          <div className="hub-thread-empty">
            <AgentIdentityMark className="hub-msg-avatar-lg" />
            <strong>Hub Assistant</strong>
            <p>Type a question, or tap the mic to talk.</p>
          </div>
        </ThreadPrimitive.Empty>
        <ThreadPrimitive.Messages
          components={{ UserMessage, AssistantMessage }}
        />
      </ThreadPrimitive.Viewport>
      <Composer />
    </ThreadPrimitive.Root>
  );
}

// Origins allowed to drive this panel via postMessage when running in embedded
// mode (i.e. inside an iframe on app.moltpod.com or a moltpod dev stack).
const EMBED_ALLOWED_PARENT_ORIGINS = new Set([
  'https://app.moltpod.com',
  'https://dev-main.moltpod.dev',
]);
const ASSISTANT_OPEN_KEY = 'hub-assistant-open';
const ASSISTANT_MODE_KEY = 'hub-assistant-mode';
const ASSISTANT_MODE_FAB = 'fab';
const ASSISTANT_MODE_SIDEBAR = 'sidebar';
const DESKTOP_SHORTCUTS = [
  { key: 'linkedin', label: 'LinkedIn', mark: 'in' },
  { key: 'google', label: 'Google', mark: 'G' },
  { key: 'luma', label: 'Luma', mark: 'lu' },
];
const FLOATING_DESKTOP_MIN_WIDTH = 360;
const FLOATING_DESKTOP_MIN_HEIGHT = 320;
const FLOATING_DESKTOP_EDGE = 12;
const ASSISTANT_POPOVER_WIDTH = 400;
const ASSISTANT_POPOVER_RIGHT = 24;
const FLOATING_DESKTOP_POPOVER_GAP = 10;
const FLOATING_DESKTOP_RECT_STORAGE_KEY = 'hub-assistant-desktop-rect';

function isAllowedParentOrigin(origin) {
  if (!origin) return false;
  if (typeof window !== 'undefined' && origin === window.location.origin) return true;
  if (EMBED_ALLOWED_PARENT_ORIGINS.has(origin)) return true;
  // Dev convenience: any host on .moltpod.dev (lets us iterate without
  // hardcoding every stack name). Tighten later if the dev domain count drops.
  try {
    const host = new URL(origin).hostname.toLowerCase();
    return host.endsWith('.moltpod.dev');
  } catch {
    return false;
  }
}

function postToParent(message) {
  if (typeof window === 'undefined' || window.parent === window) return;
  // Best-effort: post to '*' is acceptable here because every payload we send
  // is non-sensitive UI state. Inbound messages are origin-checked separately.
  try { window.parent.postMessage(message, '*'); } catch {}
}

function initialAssistantOpen(embedded) {
  if (embedded || typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(ASSISTANT_OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

function initialAssistantMode(embedded) {
  if (embedded || typeof window === 'undefined') return ASSISTANT_MODE_FAB;
  try {
    const value = window.localStorage.getItem(ASSISTANT_MODE_KEY);
    return value === ASSISTANT_MODE_SIDEBAR ? ASSISTANT_MODE_SIDEBAR : ASSISTANT_MODE_FAB;
  } catch {
    return ASSISTANT_MODE_FAB;
  }
}

function defaultFloatingDesktopRect() {
  if (typeof window === 'undefined') {
    return { left: 16, top: 96, width: 620, height: 620 };
  }
  const viewportWidth = window.innerWidth || 1280;
  const viewportHeight = window.innerHeight || 800;
  const rightLimit = floatingDesktopRightLimit();
  const height = Math.min(740, Math.max(FLOATING_DESKTOP_MIN_HEIGHT, viewportHeight - 104));
  const width = Math.min(660, Math.max(FLOATING_DESKTOP_MIN_WIDTH, rightLimit - FLOATING_DESKTOP_EDGE));
  return {
    left: Math.max(FLOATING_DESKTOP_EDGE, rightLimit - width),
    top: Math.max(FLOATING_DESKTOP_EDGE, viewportHeight - 96 - height),
    width,
    height,
  };
}

function readStoredFloatingDesktopRect() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(FLOATING_DESKTOP_RECT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const rect = {
      left: Number(parsed?.left),
      top: Number(parsed?.top),
      width: Number(parsed?.width),
      height: Number(parsed?.height),
    };
    if (!Object.values(rect).every(Number.isFinite)) return null;
    return rect;
  } catch {
    return null;
  }
}

function saveFloatingDesktopRect(rect) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(FLOATING_DESKTOP_RECT_STORAGE_KEY, JSON.stringify({
      left: Math.round(rect.left),
      top: Math.round(rect.top),
      width: Math.round(rect.width),
      height: Math.round(rect.height),
    }));
  } catch {}
}

function floatingDesktopRightLimit() {
  if (typeof window === 'undefined') return 1006;
  const viewportWidth = window.innerWidth || 1280;
  if (viewportWidth <= 860) return viewportWidth - FLOATING_DESKTOP_EDGE;
  const popoverLeft = viewportWidth - ASSISTANT_POPOVER_RIGHT - ASSISTANT_POPOVER_WIDTH;
  return Math.max(
    FLOATING_DESKTOP_EDGE + FLOATING_DESKTOP_MIN_WIDTH,
    popoverLeft - FLOATING_DESKTOP_POPOVER_GAP,
  );
}

// Drag and resize are deliberately unconstrained — the window can go
// anywhere, including over the FAB dialog or past the viewport edges.
// The only guard is recovery on load / viewport resize: if the header
// is no longer reachable (rect from a bigger screen, or parked fully
// off-screen), fall back to the default placement.
function rescueFloatingDesktopRect(rect) {
  if (typeof window === 'undefined') return rect;
  const viewportWidth = window.innerWidth || 1280;
  const viewportHeight = window.innerHeight || 800;
  const width = Math.max(rect.width, FLOATING_DESKTOP_MIN_WIDTH);
  const height = Math.max(rect.height, FLOATING_DESKTOP_MIN_HEIGHT);
  const grabbableX = rect.left + width > 56 && rect.left < viewportWidth - 56;
  const grabbableY = rect.top > -8 && rect.top < viewportHeight - 44;
  if (grabbableX && grabbableY) return { ...rect, width, height };
  return defaultFloatingDesktopRect();
}

export function DesktopPanel({ onClose, floating = false }) {
  const [opening, setOpening] = useState(null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [manualOpen, setManualOpen] = useState(false);
  const [manualUrl, setManualUrl] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [desktopFrameReady, setDesktopFrameReady] = useState(false);
  const [floatingRect, setFloatingRect] = useState(() => rescueFloatingDesktopRect(readStoredFloatingDesktopRect() || defaultFloatingDesktopRect()));
  const floatingRectRef = useRef(floatingRect);
  const desktopFrameRef = useRef(null);
  const desktopFrameFallbackTimerRef = useRef(null);

  useEffect(() => {
    floatingRectRef.current = floatingRect;
  }, [floatingRect]);

  useEffect(() => {
    if (!floating) return;
    saveFloatingDesktopRect(floatingRect);
  }, [floating, floatingRect]);

  useEffect(() => () => {
    if (desktopFrameFallbackTimerRef.current) {
      window.clearTimeout(desktopFrameFallbackTimerRef.current);
    }
  }, []);

  function wakeDesktop() {
    fetch('/api/desktop/wake', {
      method: 'POST',
      credentials: 'same-origin',
    }).catch(() => {});
  }

  useEffect(() => {
    const timers = [
      window.setTimeout(wakeDesktop, 650),
      window.setTimeout(wakeDesktop, 1800),
    ];
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, []);

  useEffect(() => {
    if (!floating) return undefined;
    const onResize = () => setFloatingRect((rect) => rescueFloatingDesktopRect(rect));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [floating]);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.source !== desktopFrameRef.current?.contentWindow) return;
      if (event.data?.type === 'hub:vnc-ready') {
        setDesktopFrameReady(true);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (!expanded && !manualOpen) return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      if (manualOpen) {
        event.preventDefault();
        setManualOpen(false);
        return;
      }
      if (expanded) {
        event.preventDefault();
        setExpanded(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded, manualOpen]);

  useEffect(() => {
    if (!floating && !expanded) return undefined;
    const onKey = (event) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey) return;
      const key = String(event.key || '').toLowerCase();
      if (key !== 'c' && key !== 'v') return;
      if (event.target?.closest?.('input, textarea, [contenteditable="true"]')) return;
      event.preventDefault();
      if (key === 'c') {
        copyDesktopClipboardToHost();
      } else {
        pasteHostClipboardToDesktop();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [floating, expanded]);

  async function openDesktopUrl({ key, label, body }) {
    if (opening) return;
    setOpening(key);
    setStatus(`Opening ${label}...`);
    setError('');
    try {
      const resp = await fetch('/api/desktop/open-url', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok === false) {
        throw new Error(payload?.error || `Could not open ${label}.`);
      }
      setStatus(`Opened ${payload?.label || label} in the pod desktop.`);
    } catch (err) {
      setStatus('');
      setError(err?.message || `Could not open ${label}.`);
    } finally {
      setOpening(null);
    }
  }

  function openShortcut(shortcut) {
    openDesktopUrl({
      key: shortcut.key,
      label: shortcut.label,
      body: { destination: shortcut.key },
    });
  }

  function openManualUrl(event) {
    event.preventDefault();
    const value = manualUrl.trim();
    if (!value) {
      setError('Enter a URL to open in the pod desktop.');
      return;
    }
    setManualOpen(false);
    openDesktopUrl({
      key: 'manual',
      label: 'manual URL',
      body: { url: value },
    });
  }

  function getDesktopClipboardBridge() {
    const frameWindow = desktopFrameRef.current?.contentWindow;
    const frameDocument = frameWindow?.document;
    const textarea = frameDocument?.getElementById?.('noVNC_clipboard_text');
    const rfb = frameWindow?.__rfb || frameWindow?.UI?.rfb || null;
    return { frameWindow, rfb, textarea };
  }

  async function copyDesktopClipboardToHost() {
    setError('');
    const { frameWindow, textarea } = getDesktopClipboardBridge();
    const text = textarea?.value || frameWindow?.__vncClipboardText || '';
    if (!navigator.clipboard?.writeText) {
      setError('Host clipboard copy is not available in this browser.');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setStatus(text ? 'Copied the desktop clipboard to your host clipboard.' : 'Desktop clipboard is empty.');
    } catch {
      setError('Allow clipboard access, then try Cmd+C again.');
    }
  }

  async function pasteHostClipboardToDesktop() {
    setError('');
    if (!navigator.clipboard?.readText) {
      setError('Host clipboard paste is not available in this browser.');
      return;
    }
    try {
      const text = await navigator.clipboard.readText();
      const { frameWindow, rfb, textarea } = getDesktopClipboardBridge();
      if (rfb?.clipboardPasteFrom) {
        rfb.clipboardPasteFrom(text);
      }
      if (textarea) {
        textarea.value = text;
        const ChangeEvent = frameWindow?.Event || Event;
        textarea.dispatchEvent(new ChangeEvent('change', { bubbles: true }));
      }
      if (frameWindow) {
        frameWindow.__vncClipboardText = text;
      }
      if (!rfb?.clipboardPasteFrom && !textarea) {
        throw new Error('noVNC clipboard bridge is not ready.');
      }
      setStatus('Pasted your host clipboard into the desktop.');
    } catch {
      setError('Allow clipboard access, then try Cmd+V again.');
    }
  }

  function beginFloatingDrag(event) {
    if (!floating || expanded || event.button !== 0 || event.target?.closest?.('button, a, input, textarea')) return;
    event.preventDefault();
    const start = {
      x: event.clientX,
      y: event.clientY,
      rect: floatingRectRef.current,
    };
    const onMove = (moveEvent) => {
      // No limits — the window follows the pointer wherever it goes.
      setFloatingRect({
        ...start.rect,
        left: start.rect.left + (moveEvent.clientX - start.x),
        top: start.rect.top + (moveEvent.clientY - start.y),
      });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp, { once: true });
  }

  function beginFloatingResize(event) {
    if (!floating || expanded || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const start = {
      x: event.clientX,
      y: event.clientY,
      rect: floatingRectRef.current,
    };
    const onMove = (moveEvent) => {
      // Bottom-right handle: the top-left corner stays put, the window
      // grows toward the pointer. Only the minimum size is enforced.
      const nextRect = {
        ...start.rect,
        width: Math.max(FLOATING_DESKTOP_MIN_WIDTH, start.rect.width + (moveEvent.clientX - start.x)),
        height: Math.max(FLOATING_DESKTOP_MIN_HEIGHT, start.rect.height + (moveEvent.clientY - start.y)),
      };
      saveFloatingDesktopRect(nextRect);
      setFloatingRect(nextRect);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp, { once: true });
  }

  function toggleExpandedDesktop() {
    setDesktopFrameReady(false);
    setExpanded((value) => !value);
  }

  function handleDesktopFrameLoad() {
    setDesktopFrameReady(false);
    wakeDesktop();
    if (desktopFrameFallbackTimerRef.current) {
      window.clearTimeout(desktopFrameFallbackTimerRef.current);
    }
    desktopFrameFallbackTimerRef.current = window.setTimeout(() => {
      setDesktopFrameReady(true);
    }, 2200);
  }

  function renderHeader({ expandedHeader = false } = {}) {
    return (
      <header
        className={`hub-assistant-desktop-header ${floating && !expandedHeader ? 'is-draggable' : ''}`}
        onPointerDown={floating && !expandedHeader ? beginFloatingDrag : undefined}
      >
        <span className="hub-assistant-desktop-header-spacer" aria-hidden="true" />
        <span className="hub-assistant-desktop-title">
          <MonitorIcon />
          Desktop
        </span>
        <span className="hub-assistant-desktop-actions">
          <button
            type="button"
            className="hub-assistant-desktop-icon-btn"
            onClick={copyDesktopClipboardToHost}
            aria-label="Copy desktop clipboard to host"
            title="Copy desktop clipboard to host (Cmd+C)"
          >
            <CopyIcon />
          </button>
          <button
            type="button"
            className="hub-assistant-desktop-icon-btn"
            onClick={pasteHostClipboardToDesktop}
            aria-label="Paste host clipboard into desktop"
            title="Paste host clipboard into desktop (Cmd+V)"
          >
            <PasteIcon />
          </button>
          <button
            type="button"
            className="hub-assistant-desktop-icon-btn"
            onClick={toggleExpandedDesktop}
            aria-label={expandedHeader ? 'Restore desktop' : 'Expand desktop'}
            title={expandedHeader ? 'Restore desktop' : 'Expand desktop'}
          >
            {expandedHeader ? <ShrinkIcon /> : <ExpandIcon />}
          </button>
          <button
            type="button"
            className="hub-assistant-desktop-icon-btn"
            onClick={onClose}
            aria-label="Hide desktop"
            title="Hide desktop"
          >
            <CloseIcon />
          </button>
        </span>
      </header>
    );
  }

  function renderFrame(className = 'hub-assistant-desktop-frame') {
    return (
      <div className={`hub-assistant-desktop-frame-wrap ${className}-wrap`}>
        <iframe
          ref={desktopFrameRef}
          className={className}
          src="/vendor/novnc/vnc_lite.html?resize=scale&path=api%2Fdesktop%2Fws"
          title="Pod desktop"
          allow="clipboard-read; clipboard-write"
          onLoad={handleDesktopFrameLoad}
        />
        {!desktopFrameReady && (
          <div className="hub-assistant-desktop-splash" role="status" aria-live="polite" aria-label="Opening desktop">
            <span className="hub-assistant-desktop-splash-text">Restoring session…</span>
          </div>
        )}
      </div>
    );
  }

  function renderControls() {
    return (
      <>
      <div className="hub-assistant-desktop-shortcuts" role="toolbar" aria-label="Open website in pod desktop">
        {DESKTOP_SHORTCUTS.map((shortcut) => {
          const isOpening = opening === shortcut.key;
          return (
            <button
              key={shortcut.key}
              type="button"
              className={`hub-assistant-desktop-shortcut hub-assistant-desktop-shortcut-${shortcut.key}`}
              onClick={() => openShortcut(shortcut)}
              disabled={Boolean(opening)}
              aria-label={`Open ${shortcut.label} in pod desktop`}
              title={`Open ${shortcut.label} in pod desktop`}
            >
              <span className="hub-assistant-desktop-shortcut-mark" aria-hidden="true">{shortcut.mark}</span>
              <span>{isOpening ? 'Opening...' : shortcut.label}</span>
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className="hub-assistant-desktop-manual"
        onClick={() => {
          setManualOpen(true);
          setError('');
        }}
        disabled={Boolean(opening)}
      >
        Enter manual URL
      </button>
      {(status || error) && (
        <div className={`hub-assistant-desktop-status ${error ? 'is-error' : ''}`} role="status">
          {error || status}
        </div>
      )}
      {manualOpen && (
        <div className="hub-assistant-desktop-dialog-backdrop" role="presentation">
          <form className="hub-assistant-desktop-dialog" onSubmit={openManualUrl} role="dialog" aria-modal="true" aria-label="Open manual URL">
            <h3>Open URL</h3>
            <input
              value={manualUrl}
              onChange={(event) => setManualUrl(event.target.value)}
              placeholder="example.com or https://example.com"
              autoFocus
              inputMode="url"
            />
            <div className="hub-assistant-desktop-dialog-actions">
              <button type="button" onClick={() => setManualOpen(false)}>Cancel</button>
              <button type="submit">Open</button>
            </div>
          </form>
        </div>
      )}
      </>
    );
  }

  return (
    <section
      className={`hub-assistant-desktop-panel ${floating ? 'is-floating' : ''} ${expanded ? 'is-expanded' : ''}`}
      aria-label="Desktop"
      style={floating ? {
        left: `${floatingRect.left}px`,
        top: `${floatingRect.top}px`,
        width: `${floatingRect.width}px`,
        height: `${floatingRect.height}px`,
      } : undefined}
    >
      {renderHeader()}
      {expanded ? (
        <div className="hub-assistant-desktop-expanded-placeholder" aria-hidden="true" />
      ) : renderFrame()}
      {!expanded && renderControls()}
      {floating && !expanded && (
        <span
          className="hub-assistant-desktop-resize-handle"
          aria-hidden="true"
          onPointerDown={beginFloatingResize}
        />
      )}
      {expanded && (
        <div className="hub-assistant-desktop-modal-backdrop" role="presentation">
          <section className="hub-assistant-desktop-modal" role="dialog" aria-modal="true" aria-label="Expanded desktop">
            {renderHeader({ expandedHeader: true })}
            {renderFrame('hub-assistant-desktop-modal-frame')}
            {renderControls()}
          </section>
        </div>
      )}
    </section>
  );
}

export function AssistantPanel({ embedded = false } = {}) {
  // In embedded mode the host owns visibility. Standalone mode restores the
  // last visible FAB state so same-origin navigation does not collapse it.
  const [open, setOpen] = useState(() => initialAssistantOpen(embedded));
  const [pageContext, setPageContext] = useState(null);
  const [desktopOpen, setDesktopOpen] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [mode, setMode] = useState(() => initialAssistantMode(embedded));
  const runtime = useLocalRuntime(podAdapter);

  useEffect(() => {
    if (embedded) return;
    try {
      window.localStorage.setItem(ASSISTANT_MODE_KEY, mode);
    } catch {}
  }, [embedded, mode]);

  const toggleMode = () => {
    setMode((m) => (m === ASSISTANT_MODE_SIDEBAR ? ASSISTANT_MODE_FAB : ASSISTANT_MODE_SIDEBAR));
  };
  // Sidebar dock mode is temporarily disabled; FAB stays as the only layout.
  const sidebarMode = false;

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const cls = 'is-hub-sidebar-docked';
    if (sidebarMode && open) document.body.classList.add(cls);
    else document.body.classList.remove(cls);
    return () => document.body.classList.remove(cls);
  }, [sidebarMode, open]);
  useEffect(() => {
    const id = getStoredConversationId();
    if (!id) return;
    let cancelled = false;
    fetchHistoryMessages(id).then((msgs) => {
      if (cancelled || !msgs.length) return;
      rememberThreadMessages(msgs);
      try { runtime.thread?.reset(msgs); } catch {}
    });
    return () => { cancelled = true; };
  }, [runtime]);

  const { agent, loading: agentLoading, avatarReady } = useHubAgentState({ preloadAvatar: true });
  const standaloneDesktopAvailable = !embedded;

  useEffect(() => {
    if (embedded) return;
    try {
      window.sessionStorage.setItem(ASSISTANT_OPEN_KEY, open ? '1' : '0');
    } catch {}
  }, [embedded, open]);

  useEffect(() => {
    if (!open) setDesktopOpen(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let controller = null;
    const checkSession = ({ initial = false } = {}) => {
      if (controller) controller.abort();
      controller = new AbortController();
      if (initial) setSessionReady(false);
      ensureAgentSessionFresh(controller.signal, {
        minExpiresInSeconds: AGENT_SESSION_REFRESH_THRESHOLD_SECONDS,
      }).then((ok) => {
        if (!controller.signal.aborted) setSessionReady((prev) => prev || Boolean(ok));
      }).catch(() => {
        if (!controller.signal.aborted) setSessionReady(true);
      });
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkSession();
    };
    checkSession({ initial: true });
    const interval = window.setInterval(checkSession, 180000);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(interval);
      if (controller) controller.abort();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    if (embedded) {
      // In embed mode the host page owns context. Do not clear it when the
      // panel opens, because the host may have sent context while closed.
      return undefined;
    }
    let controller = null;
    let requestId = 0;
    const refreshContext = () => {
      requestId += 1;
      const localRequestId = requestId;
      if (controller) controller.abort();
      controller = new AbortController();
      setPageContext(null);
      setCurrentHubPageContext(null);
      collectHubPageContext(controller.signal).then((context) => {
        if (controller.signal.aborted || localRequestId !== requestId) return;
        setPageContext(context);
        setCurrentHubPageContext(context);
      });
    };
    refreshContext();
    window.addEventListener('hub:soft-navigation-complete', refreshContext);
    window.addEventListener('popstate', refreshContext);
    window.addEventListener('hashchange', refreshContext);
    window.addEventListener('hub:assistant-action', refreshContext);
    return () => {
      if (controller) controller.abort();
      window.removeEventListener('hub:soft-navigation-complete', refreshContext);
      window.removeEventListener('popstate', refreshContext);
      window.removeEventListener('hashchange', refreshContext);
      window.removeEventListener('hub:assistant-action', refreshContext);
    };
  }, [open, embedded]);

  useEffect(() => {
    if (!open) return;
    const setDesktopState = (state) => {
      if (embedded) {
        postToParent({ type: state === 'toggle' ? 'mlp:assistant:desktop:toggle' : 'mlp:assistant:desktop:state', open: state === 'open' });
      } else {
        setDesktopOpen((v) => state === 'toggle' ? !v : state === 'open');
      }
    };
    const onKey = (e) => {
      const k = (e.key || '').toLowerCase();
      if (e.key === 'Escape') {
        if (embedded) {
          postToParent({ type: 'mlp:assistant:close' });
        } else {
          setOpen(false);
        }
      } else if (k === 'u' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setDesktopState('toggle');
      }
    };
    const onAssistantAction = (e) => {
      if (e?.detail?.type === 'desktop_toggle') setDesktopState('toggle');
      if (e?.detail?.type === 'desktop_open') setDesktopState('open');
      if (e?.detail?.type === 'desktop_close') setDesktopState('close');
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('hub:assistant-action', onAssistantAction);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('hub:assistant-action', onAssistantAction);
    };
  }, [open, embedded]);

  // Cmd/Ctrl+I global toggle — open the FAB from anywhere (or notify parent
  // when running inside an iframe so the host can manage panel visibility).
  useEffect(() => {
    const onKey = (e) => {
      const k = (e.key || '').toLowerCase();
      if (k === 'i' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (embedded) {
          postToParent({ type: 'mlp:assistant:toggle' });
        } else {
          setOpen((v) => !v);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [embedded]);

  // Embed-mode plumbing: announce ready, accept toggle/open/close/context
  // messages from the host, and report state changes back so the host can
  // size the iframe.
  useEffect(() => {
    if (!embedded) return undefined;
    const onMessage = (event) => {
      if (!isAllowedParentOrigin(event.origin)) return;
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      switch (data.type) {
        case 'mlp:assistant:open':
          setOpen(true);
          break;
        case 'mlp:assistant:close':
          setOpen(false);
          break;
        case 'mlp:assistant:toggle':
          setOpen((v) => !v);
          break;
        case 'mlp:assistant:context':
          if (data.payload && typeof data.payload === 'object') {
            const ctx = buildInjectedHubPageContext(data.payload);
            if (ctx) {
              setPageContext(ctx);
              setCurrentHubPageContext(ctx);
            }
          }
          break;
        case 'mlp:assistant:desktop:state':
          setDesktopOpen(Boolean(data.open));
          break;
        default:
          break;
      }
    };
    window.addEventListener('message', onMessage);
    postToParent({ type: 'mlp:assistant:ready' });
    postToParent({ type: 'mlp:assistant:context-request' });
    return () => window.removeEventListener('message', onMessage);
  }, [embedded]);

  useEffect(() => {
    if (!embedded) return;
    postToParent({ type: 'mlp:assistant:state', open });
  }, [embedded, open]);

  return (
    <HubAgentContext.Provider value={agent}>
      <AssistantRuntimeProvider runtime={runtime}>
        {!embedded && (
          <button
            type="button"
            className={`hub-assistant-fab ${open ? 'is-open' : ''} ${sidebarMode ? 'is-sidebar' : ''}`}
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Close assistant' : 'Open assistant'}
            aria-expanded={open}
            title={open ? 'Close assistant (⌘/Ctrl+I)' : 'Open assistant (⌘/Ctrl+I)'}
          >
            {open ? <CloseIcon /> : <AgentAvatar agent={agent} className="hub-assistant-fab-avatar" />}
          </button>
        )}
        {!embedded && (
          <span
            className={`hub-assistant-fab-hint ${open ? 'is-hidden' : ''}`}
            aria-hidden="true"
          >⌘I</span>
        )}
        {open && (
          <div
            className={`hub-assistant-shell ${desktopOpen && !embedded ? 'has-desktop has-floating-desktop' : ''} ${embedded ? 'is-embedded' : ''} ${sidebarMode ? 'is-sidebar' : ''}`}
            role="dialog"
            aria-label="Assistant"
          >
            {desktopOpen && !embedded && <DesktopPanel floating onClose={() => setDesktopOpen(false)} />}
            <div className={`hub-assistant-popover ${embedded ? 'is-embedded' : 'is-docked'} ${sidebarMode ? 'is-sidebar' : ''}`}>
              <header className="hub-assistant-header">
                <span className="hub-assistant-title">
                  <AgentAvatar agent={agent} className="hub-assistant-header-avatar" />
                  {agent?.display_name || 'Assistant'}
                </span>
                <span className="hub-assistant-tag">live</span>
              </header>
              <ConversationSwitcher
                runtime={runtime}
                variant="panel"
                mode={embedded ? null : mode}
                onToggleMode={null}
              />
              {pageContext && (
                <div className="hub-assistant-context-banner" title={pageContext.summary}>
                  <span className="hub-assistant-context-dot" aria-hidden="true" />
                  <span className="hub-assistant-context-label">{pageContext.label}</span>
                  {((embedded && pageContext.desktop_available) || standaloneDesktopAvailable) && (
                    <button
                      type="button"
                      className="hub-assistant-context-action"
                      onClick={() => (
                        embedded
                          ? postToParent({ type: 'mlp:assistant:desktop:toggle' })
                          : setDesktopOpen((v) => !v)
                      )}
                      aria-pressed={desktopOpen}
                      title={desktopOpen ? 'Hide desktop (⌘U / Ctrl+U)' : 'Show desktop (⌘U / Ctrl+U)'}
                    >
                      <MonitorIcon />
                      <span>{desktopOpen ? 'Hide desktop' : 'Show desktop'}</span>
                      <kbd className="hub-assistant-shortcut">⌘U</kbd>
                    </button>
                  )}
                </div>
              )}
              {sessionReady && !agentLoading && avatarReady ? (
                <Thread />
              ) : (
                <div className="hub-assistant-ready" role="status">
                  <span>Preparing your agent...</span>
                </div>
              )}
            </div>
          </div>
        )}
      </AssistantRuntimeProvider>
    </HubAgentContext.Provider>
  );
}
